import React, { useEffect } from "react";
import { Image, ModalProps } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import "./ForfeitGameModal.scss";
import GhostButton from "../../dashboard-page/GhostButton/GhostButton";
import ImageClose from "../../../images/close-icon-purple.svg";
import FilledButton, {
  ButtonSizes,
} from "../../dashboard-page/FilledButton/FilledButton";
import CloseRoundedIcon from "../../icons/CloseRoundedIcon";
import { CustomSpinner } from "../../CustomSpinner/CustomSpinner";
import useForfeitTournamentEntryMutation from "../../../react-query/mutations/use-forfeit-tournament-entry";
import HyperCoin from "../../HyperCoin/HyperCoin";
import BottomDrawer from "../../BottomDrawer/BottomDrawer";
import useAnalytics from "../../../hooks/useAnalytics";
import { GameType } from "../../../utils/constants";
import useAuth from "../../../hooks/useAuth";
import useHprFiatFormatter from "../../../hooks/useHprFiatFormatter";

interface ForfeitGameModalProps extends Pick<ModalProps, "show"> {
  redirectUrl?: string;
  entryId: string;
  thumbnail?: string;
  stakeAmount: number;
  gameName: string;
  gameType: GameType;
  refetch?: () => void;
  onHide: () => void;
}

function ForfeitGameModal({
  show,
  onHide,
  redirectUrl,
  entryId,
  thumbnail,
  stakeAmount,
  gameName,
  gameType,
  refetch,
}: ForfeitGameModalProps) {
  const { t } = useTranslation();
  const analytics = useAnalytics();
  const { userProfile } = useAuth();
  const { isFiatDisplay, formatHprToFiatValue } = useHprFiatFormatter();
  const {
    forfeitEntry,
    mutation: { isPending, isSuccess },
  } = useForfeitTournamentEntryMutation();

  useEffect(() => {
    if (!isSuccess) return;
    analytics.sendCancelGameEvent({
      entry_fee: stakeAmount,
      game_name: gameName,
      game_mode: gameType,
    });
    refetch && refetch();
  }, [isSuccess, analytics, gameName, gameType, stakeAmount, refetch]);

  return (
    <BottomDrawer
      isOpen={!!show}
      onClose={onHide}
      showCloseBtn={false}
      containerClass="px-0 pt-0"
    >
      <div className="forfeit-game-modal p-3">
        <p className="d-flex justify-content-end">
          <GhostButton aria-label="close" onClick={onHide}>
            <Image src={ImageClose} alt="" width={30} height={30} />
          </GhostButton>
        </p>
        <div>
          <div className="img-box">
            <Image
              src={userProfile?.avatar?.url}
              alt={userProfile?.avatar?.label}
              height={60}
              width={60}
              className="user-avatar"
            />
            <CloseRoundedIcon className="cancel-icon" />
            <Image
              src={thumbnail}
              alt={`${gameName} thumbnail`}
              height={60}
              width={60}
              className="game-thumbnail"
            />
          </div>
          <h2 className="heading">{t("forfeit_game")}</h2>
          <p className="content">
            {t("are_you_sure_you_want_to_forfeit_this_game_key")}
          </p>
          <FilledButton
            fullwidth
            size={ButtonSizes.MD}
            className="mb-1 text-capitalize"
            onClick={onHide}
          >
            {t("no_go_back")}
          </FilledButton>
          <GhostButton
            className="cancel-btn"
            onClick={() => forfeitEntry(entryId)}
            disabled={isPending}
          >
            <span className="d-flex align-items-center gap-1">
              <span>{t("forfeit_game")}</span>
              <span>
                {isFiatDisplay ? (
                  <>-{formatHprToFiatValue(stakeAmount)}</>
                ) : (
                  <>
                    <HyperCoin /> -{stakeAmount}
                  </>
                )}
              </span>
            </span>

            {isPending && <CustomSpinner size="sm" className="ms-1" />}
          </GhostButton>
        </div>
      </div>
    </BottomDrawer>
  );
}

export default ForfeitGameModal;
